/**
 * UpgradeRequestBanner - Banner shown to owners when team members request an upgrade
 * Supports multiple pending requests with aggregation
 * Displays at the top of the app until dismissed or upgrade is completed
 */

import React, { useState, useEffect } from 'react';
import { X, ArrowRight, Users, Loader2, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import { cn } from '../../lib/utils';
import { billingApi } from '../../services/api';
import { useNavigate } from 'react-router-dom';

const UpgradeRequestBanner = ({ className }) => {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDismissing, setIsDismissing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    fetchPendingRequests();
  }, []);
  
  const fetchPendingRequests = async () => {
    try {
      const response = await billingApi.getPendingUpgradeRequests();
      if (response.data?.requests?.length > 0) {
        setRequests(response.data.requests);
      }
    } catch (error) {
      console.error('Failed to fetch pending upgrade requests:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDismiss = async (requestId = null) => {
    setIsDismissing(true);
    try {
      if (requestId) {
        // Dismiss specific request
        await billingApi.dismissUpgradeRequest(requestId);
        setRequests(prev => prev.filter(r => r.request_id !== requestId));
      } else {
        // Dismiss all
        await billingApi.dismissAllUpgradeRequests();
        setRequests([]);
      }
    } catch (error) {
      console.error('Failed to dismiss upgrade request:', error);
    } finally {
      setIsDismissing(false);
    }
  };
  
  const handleUpgradeClick = () => {
    navigate('/settings?tab=billing');
  };
  
  const handleTeamClick = () => {
    navigate('/team');
  };
  
  if (isLoading || requests.length === 0) return null;
  
  const limitLabels = {
    products: 'product limit',
    features: 'feature limit',
    seats: 'team seat limit',
    ai_usage: 'AI usage limit',
  };
  
  // Get unique requesters
  const uniqueRequesters = [...new Set(requests.map(r => r.requester_name || r.requester_email))];
  const requestCount = requests.length;
  const latestRequest = requests[0]; // Assuming sorted by date
  
  // Format time
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const hours = Math.floor((now - date) / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    return 'Just now';
  };
  
  return (
    <div className={cn(
      "bg-gradient-to-r from-purple-600 to-indigo-600 text-white",
      className
    )}>
      {/* Main banner */}
      <div className="px-4 py-2">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5" />
            <div className="text-sm">
              {requestCount === 1 ? (
                <span>
                  <span className="font-semibold">{latestRequest.requester_name || latestRequest.requester_email}</span>
                  {' '}hit the {limitLabels[latestRequest.limit_type] || 'plan limit'} and is requesting an upgrade.
                  {latestRequest.message && (
                    <span className="opacity-80 ml-1">&ldquo;{latestRequest.message}&rdquo;</span>
                  )}
                </span>
              ) : (
                <span>
                  <span className="font-semibold">{requestCount} team members</span>
                  {' '}are waiting for an upgrade
                  <button 
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="ml-2 opacity-80 hover:opacity-100 inline-flex items-center"
                  >
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                </span>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={handleUpgradeClick}
              className="flex items-center gap-1 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-md text-sm font-medium transition-colors"
            >
              Review Options
              <ArrowRight className="w-4 h-4" />
            </button>
            
            {requestCount > 1 && (
              <button
                onClick={handleTeamClick}
                className="flex items-center gap-1 px-3 py-1 bg-white/10 hover:bg-white/20 rounded-md text-sm transition-colors"
              >
                <Users className="w-4 h-4" />
                Team
              </button>
            )}
            
            <button
              onClick={() => handleDismiss()}
              disabled={isDismissing}
              className="p-1 hover:bg-white/20 rounded-md transition-colors"
              title={requestCount > 1 ? "Dismiss all" : "Dismiss"}
            >
              {isDismissing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <X className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Expanded request list */}
      {isExpanded && requestCount > 1 && (
        <div className="bg-purple-700/50 border-t border-purple-500/30">
          <div className="max-w-7xl mx-auto px-4 py-2">
            <div className="space-y-2">
              {requests.map((req) => (
                <div 
                  key={req.request_id || req.created_at}
                  className="flex items-center justify-between text-sm bg-white/10 rounded-md px-3 py-2"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-xs font-medium">
                      {(req.requester_name || req.requester_email || '?')[0].toUpperCase()}
                    </div>
                    <div>
                      <span className="font-medium">{req.requester_name || req.requester_email}</span>
                      <span className="opacity-70 ml-2">• {limitLabels[req.limit_type] || req.limit_type}</span>
                      {req.message && (
                        <p className="text-xs opacity-70 mt-0.5">&ldquo;{req.message}&rdquo;</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs opacity-70 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatTime(req.created_at)}
                    </span>
                    <button
                      onClick={() => handleDismiss(req.request_id)}
                      className="p-1 hover:bg-white/20 rounded transition-colors"
                      title="Dismiss this request"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpgradeRequestBanner;
